/**
 * Shared authorized fetch for the merchant-app (owner + staff areas).
 *
 * Extracted from `staff.ts` so the owner hooks (`owner.ts`) can reuse the
 * same auth flow without duplication. Mirrors the pattern used by
 * `features/kds/api.ts` and `features/orders/hooks.ts`.
 *
 * Exports: `merchantApiFetch`, `toQuery`, `compact`, `type FetchInit`.
 */
import { ApiError, type ApiErrorPayload } from './errors';
import { clearTokens, getAccessToken } from '../auth/tokens';
import { tryRefresh } from '../auth/refresh';
import { forceLogin } from '../auth/keycloak';

const baseUrl = import.meta.env.VITE_API_BASE ?? '';

/** Optional method/body for write requests (PATCH/POST/PUT/DELETE); GET when omitted. */
export interface FetchInit {
  method?: string;
  body?: unknown;
}

const parseErrorPayload = async (r: Response): Promise<ApiErrorPayload> => {
  try {
    const b = (await r.clone().json()) as unknown;
    if (typeof b === 'object' && b !== null) return b as ApiErrorPayload;
  } catch {
    /* fall through to statusText */
  }
  return { message: r.statusText };
};

const sendWithAuth = (path: string, init?: FetchInit): Promise<Response> => {
  const token = getAccessToken();
  const headers = new Headers();
  headers.set('Accept', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const reqInit: RequestInit = { headers };
  if (init?.method) {
    reqInit.method = init.method;
    if (init.body !== undefined) {
      headers.set('Content-Type', 'application/json');
      reqInit.body = JSON.stringify(init.body);
    }
  }
  return fetch(`${baseUrl}${path}`, reqInit);
};

/** Backoff do retry de 429 — mesmo escalonamento do `client.ts` gerado. */
const RETRY_429_DELAYS_MS = [1000, 2000, 4000];

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Authorized JSON fetch: one-shot 401 refresh, retry com backoff em 429
 * (1s/2s/4s — paridade com o middleware do `client.ts`), ApiError envelope,
 * 204→undefined.
 *
 * Defaults to GET. Pass `{ method, body }` for writes; `body` is JSON-encoded
 * and `Content-Type: application/json` is set automatically.
 */
export const merchantApiFetch = async <T,>(path: string, init?: FetchInit): Promise<T> => {
  let r = await sendWithAuth(path, init);

  if (r.status === 401) {
    if (await tryRefresh()) {
      r = await sendWithAuth(path, init);
    } else {
      clearTokens();
      forceLogin(); // OIDC → kc.login(); sem instância KC → raiz (PanelGuard assume).
      throw new ApiError(401, { message: 'Session expired' }, 'Session expired');
    }
  }

  for (const delay of RETRY_429_DELAYS_MS) {
    if (r.status !== 429) break;
    await sleep(delay);
    r = await sendWithAuth(path, init);
  }

  if (!r.ok) throw new ApiError(r.status, await parseErrorPayload(r), `HTTP ${r.status}`);
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
};

/** Build a `?a=b&c=d` suffix from a string map (already URL-safe-encoded). */
export const toQuery = (params: Record<string, string>): string => {
  const s = new URLSearchParams(params).toString();
  return s ? `?${s}` : '';
};

/** Drop `undefined`/`null` entries; stringify the rest. Stable insertion order. */
export const compact = (
  entries: Array<[string, string | number | boolean | undefined | null]>,
): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [k, v] of entries) {
    if (v === undefined || v === null) continue;
    out[k] = String(v);
  }
  return out;
};
